import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { NavLink, Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { api } from "../api/client";
import { useAuth } from "../context/AuthContext";
import { apiFetch } from "../utils/apiFetch";

export default function InspectionDetail() {
  const { inspectionId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const [inspection, setInspection] = useState(null);
  const [template, setTemplate] = useState(null);

  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  const [pdfLoading, setPdfLoading] = useState(null);
  const [emailModal, setEmailModal] = useState(null); // { reportType, vesselName, round, redirectToInspections }
  const [sendModal, setSendModal] = useState(null);   // { reportType } — open when report is ready to send
  const [attendees, setAttendees] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState(new Set());
  const [adHocName, setAdHocName] = useState("");
  const [adHocEmail, setAdHocEmail] = useState("");
  const [adHocList, setAdHocList] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null); // { sent, failed, message }

  // Summary modal state
  const [summaryModal, setSummaryModal] = useState(null); // { mode: "required"|"optional", onConfirm }
  const [summaryDraft, setSummaryDraft] = useState("");

  // ----- Answers state + autosave queue (required by QuestionsPanel) -----
  const { user } = useAuth(); // may be undefined depending on AuthContext

  // Derive user id from dev token if AuthContext user isn't populated (supports "dev-13")
  const derivedUserId = useMemo(() => {
    if (user?.id != null) return Number(user.id);
    const m = String(token || "").match(/^dev-(\d+)$/);
    return m ? Number(m[1]) : null;
  }, [token, user]);

  const ctxUser = useMemo(() => {
    if (user) return { ...user, id: derivedUserId ?? user.id };
    return derivedUserId != null ? { id: derivedUserId } : null;
  }, [user, derivedUserId]);

  // Map<question_id:number, answerRow:object>
  const [answersByQid, setAnswersByQid] = useState(() => new Map());

  // *** FIX 1: Keep a ref that always points to the latest answersByQid.
  // flushSave reads from this ref so it never captures a stale closure. ***
  const answersByQidRef = useRef(answersByQid);
  useEffect(() => {
    answersByQidRef.current = answersByQid;
  }, [answersByQid]);

  // Keep a ref to the latest template too, for the same reason.
  const templateRef = useRef(template);
  useEffect(() => {
    templateRef.current = template;
  }, [template]);

  // Keep a ref to ctxUser to avoid stale closure in flushSave.
  const ctxUserRef = useRef(ctxUser);
  useEffect(() => {
    ctxUserRef.current = ctxUser;
  }, [ctxUser]);

  // tracks which question_ids have unsaved changes
  const dirtyRef = useRef(new Set());
  const saveTimerRef = useRef(null);

  function mergeAnswerRow(row) {
    if (!row || !row.question_id) return;

    const qid = Number(row.question_id);

    setAnswersByQid((prev) => {
      const next = new Map(prev);
      const cur = next.get(qid) || {};

      // Normalize answer id field
      const answerId = row.answer_id ?? row.id ?? cur.answer_id ?? cur.id ?? null;

      // Only overwrite with non-null/defined values from backend
      const cleaned = Object.fromEntries(
        Object.entries(row).filter(([, v]) => v !== undefined && v !== null)
      );

      next.set(qid, {
        ...cur,
        ...cleaned,
        // ensure both keys exist for consumers
        id: answerId ?? cur.id ?? null,
        answer_id: answerId ?? cur.answer_id ?? null,
      });

      return next;
    });
  }

  // *** FIX 2: flushSave is now a stable useCallback that reads live data
  // exclusively through refs — no stale closure over answersByQid or template. ***
  const flushSave = useCallback(async () => {
    // cancel any pending timer and take a snapshot of dirty ids
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }

    const dirtyIds = Array.from(dirtyRef.current);
    dirtyRef.current.clear();

    if (!dirtyIds.length) return;

    // push updates one by one (simple + reliable)
    for (const qid of dirtyIds) {
      // *** Read from ref, not the stale closure variable ***
      const row = answersByQidRef.current.get(Number(qid));
      if (!row) continue;

      try {
        const currentTemplate = templateRef.current;
        const question =
          currentTemplate?.sections
            ?.flatMap((sec) => sec.questions || [])
            ?.find((q) => Number(q.id) === Number(qid));

        const questionType = question?.type || question?.question_type || null;

        let valueChoice = null;
        let valueBool = null;
        let valueNumeric = null;
        let valueText = null;
        let valueMultiChoice = null;

        if (questionType === "yes_no") {
          valueBool =
            row.value_bool === true
              ? true
              : row.value_bool === false
              ? false
              : null;
          if (valueBool === null) continue;

        } else if (questionType === "yes_no_na") {
          valueChoice = row.value_choice ?? null;

        } else if (questionType === "numeric") {
          valueNumeric = row.value_numeric ?? null;

        } else if (questionType === "text" || questionType === "date") {
          valueText = row.value_text ?? null;

        } else if (questionType === "single_choice") {
          valueChoice = row.value_choice ?? null;

        } else if (questionType === "multi_choice") {
          const raw = row.value_multi_choice ?? null;
          if (raw) {
            try {
              valueMultiChoice = Array.isArray(raw) ? raw : JSON.parse(raw);
            } catch {
              valueMultiChoice = null;
            }
          }

        } else {
          valueChoice = row.value_choice ?? null;
          valueBool = row.value_bool ?? null;
        }

        const payload = {
          answers: [
            {
              question_id: Number(qid),
              value_choice: valueChoice,
              value_bool: valueBool,
              value_numeric: valueNumeric,
              value_text: valueText,
              value_multi_choice: valueMultiChoice,
              comment: row.comment ?? "",
              created_by_user_id: ctxUserRef.current?.id ?? null,
              spec_id: row.spec_id ?? question?.spec_id ?? null,
              section_id: row.section_id ?? question?.section_id ?? null,
            },
          ],
        };

        const resp = await api(`/india/inspections/${inspectionId}/answers`, {
          method: "POST",
          token,
          body: payload,
        });

        // Backend can return:
        // - { answers: [...] }
        // - { saved, inspection_id, findings, answers: [...] }
        // - { answer: {...} }
        const returnedAnswers =
          resp?.answers ||
          (resp?.answer ? [resp.answer] : null);

        if (Array.isArray(returnedAnswers)) {
          for (const r of returnedAnswers) mergeAnswerRow(r);
        } else {
          // don't corrupt state; re-queue for retry
          dirtyRef.current.add(Number(qid));
          setErr("Unexpected /answers response shape");
        }
      } catch (e) {
        setErr(e?.message || "Failed saving answer");
        // re-mark as dirty so it can be retried
        dirtyRef.current.add(Number(qid));
      }
    }
  // inspectionId and token are stable for the lifetime of this page, safe deps
  }, [inspectionId, token]);

  const queueSave = useCallback((questionId, patch) => {
    const qid = Number(questionId);

    // optimistic local update
    setAnswersByQid((prev) => {
      const next = new Map(prev);
      const cur = next.get(qid) || { question_id: qid };
      next.set(qid, { ...cur, ...patch, question_id: qid });
      return next;
    });

    dirtyRef.current.add(qid);

    // debounce flush
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(() => {
      flushSave();
    }, 800);
  }, [flushSave]);

  // cleanup timer on unmount
  useEffect(() => {
    return () => {
      const t = saveTimerRef.current;
      if (t) clearTimeout(t);
    };
  }, []);
  // ----- End answers state + autosave queue -----

  // If user hits /inspections/:id (no subroute), redirect to /questions
  const isIndexPath =
    location.pathname === `/inspections/${inspectionId}` ||
    location.pathname === `/inspections/${inspectionId}/`;

  useEffect(() => {
    if (!inspectionId || !token) return;

    let cancelled = false;

    (async () => {
      setLoading(true);
      setErr(null);

      try {
        const res = await api(`/india/inspections/${inspectionId}`, { token });
        const answersRes = await api(`/india/inspections/${inspectionId}/answers`, { token });

        if (cancelled) return;

        setInspection(res?.inspection || null);
        setTemplate(res?.template || null);

        const answersItems = Array.isArray(answersRes?.items) ? answersRes.items : [];
        const map = new Map();

        for (const row of answersItems) {
          if (!row?.question_id) continue;
          map.set(Number(row.question_id), row);
        }

        setAnswersByQid(map);

        if (!res?.inspection) {
          setErr("Inspection payload missing 'inspection' object.");
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e?.message || "Failed to load inspection");
        setInspection(null);
        setTemplate(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [inspectionId, token]);

  const title = useMemo(() => {
    if (!inspection) return `Inspection #${inspectionId}`;

    if (inspection.name) return inspection.name;

    const vesselLabel =
      inspection.vessel_name ||
      inspection.vessel?.name ||
      (inspection.vessel_id ? `Vessel ${inspection.vessel_id}` : null);

    return vesselLabel || `Inspection #${inspectionId}`;
  }, [inspection, inspectionId]);

  async function refreshInspection() {
    try {
      const res = await api(`/india/inspections/${inspectionId}`, { token });
      setInspection(res?.inspection || null);
    } catch { /* silent — page still works */ }
  }

  async function openSendModal(reportType) {
    setSendResult(null);
    setAdHocList([]);
    setAdHocName("");
    setAdHocEmail("");
    try {
      const res = await api(`/india/inspections/${inspectionId}/attendees`, { token });
      const raw = Array.isArray(res) ? res : (res?.attendees || []);
      const list = raw.filter(a => a.email);
      setAttendees(list);
      setSelectedEmails(new Set(list.map(a => a.email)));
    } catch {
      setAttendees([]);
      setSelectedEmails(new Set());
    }
    setSendModal({ reportType });
  }

  async function handleSendReport() {
    if (!sendModal) return;
    setSending(true);
    setSendResult(null);
    try {
      const attendeeRecipients = attendees
        .filter(a => a.email && selectedEmails.has(a.email))
        .map(a => ({ name: a.name || a.email, email: a.email }));
      const allRecipients = [...attendeeRecipients, ...adHocList];
      if (allRecipients.length === 0) {
        setSendResult({ sent: 0, failed: [], message: "No recipients selected." });
        setSending(false);
        return;
      }
      const result = await api(`/india/inspections/${inspectionId}/send-report`, {
        token,
        method: "POST",
        body: { report_type: sendModal.reportType, recipients: allRecipients },
      });
      setSendResult(result);
      setTimeout(() => { setSendModal(null); setSendResult(null); navigate(`/inspections/${inspectionId}/evidence`); }, 2500);
    } catch (e) {
      setSendResult({ sent: 0, failed: [], message: e?.message || "Send failed." });
    } finally {
      setSending(false);
    }
  }

  function addAdHoc() {
    if (!adHocEmail.trim() || !adHocName.trim()) return;
    const email = adHocEmail.trim().toLowerCase();
    if (adHocList.find(r => r.email === email)) return;
    setAdHocList(prev => [...prev, { name: adHocName.trim(), email }]);
    setAdHocName("");
    setAdHocEmail("");
  }

  function removeAdHoc(email) {
    setAdHocList(prev => prev.filter(r => r.email !== email));
  }

  function toggleAttendee(email) {
    setSelectedEmails(prev => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  // Opens summary modal before generating a report.
  // mode "required" = inspection report (first generation, summary strongly encouraged)
  // mode "optional" = interim / final (may already have a summary)
  function requestSummary(mode, onConfirm) {
    setSummaryDraft(inspection?.summary_text || "");
    setSummaryModal({ mode, onConfirm });
  }

  async function saveSummaryAndProceed() {
    const text = summaryDraft.trim();
    if (text !== (inspection?.summary_text || "").trim()) {
      // Save to backend
      try {
        await api(`/india/inspections/${inspectionId}/setup`, {
          token,
          method: "PATCH",
          body: { summary_text: text || null },
        });
        setInspection((prev) => ({ ...prev, summary_text: text || null }));
      } catch {
        // non-fatal — proceed anyway
      }
    }
    const cb = summaryModal?.onConfirm;
    setSummaryModal(null);
    cb?.();
  }

  async function openPdf(kind, downloadOnly = false) {
    if (!token) {
      setErr("Not authenticated (missing token). Please login again.");
      return;
    }

    try {
      setErr(null);
      setPdfLoading(kind);

      const url = `/india/inspections/${inspectionId}/report/${kind}`;
      const baseUrl = (process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
      const res = await apiFetch(`${baseUrl}${url}`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/pdf",
        },
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        throw new Error(msg || `PDF request failed (${res.status})`);
      }

      const blob = await res.blob();
      const pdfUrl = URL.createObjectURL(blob);

      // Attempt to open in new tab — may be blocked on mobile, that's OK
      try { window.open(pdfUrl, "_blank", "noopener,noreferrer"); } catch {}

      // Use server filename from Content-Disposition, fall back to local name
      const disposition = res.headers.get("content-disposition") || "";
      const match = disposition.match(/filename="?([^";\n]+)"?/);
      const serverFilename = match?.[1]?.trim() || null;
      const inspName = inspection?.name || `inspection_${inspectionId}`;
      const safe = inspName.replace(/[^a-z0-9 _\-–—]/gi, "").replace(/\s+/g, " ").trim().replace(/ /g, "_");
      const a = document.createElement("a");
      a.href = pdfUrl;
      a.download = serverFilename || (kind === "inspection" ? `Inspection-Report_${safe}.pdf` : `Final-Inspection-Report_${safe}.pdf`);
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);

      setTimeout(() => URL.revokeObjectURL(pdfUrl), 60_000);

      // Re-fetch and open send modal — always, regardless of whether window.open worked
      if (kind === "inspection") {
        await refreshInspection();
        openSendModal("inspection");
      }
      if (kind === "final") {
        openSendModal("final");
      }
    } catch (e) {
      setErr(e?.message || "Failed to open PDF");
      // Even on error, re-fetch inspection state so tabs update correctly on mobile
      if (kind === "inspection") {
        await refreshInspection();
      }
    } finally {
      setPdfLoading(null);
    }
  }

  if (!token) return <Navigate to="/login" replace />;
  if (isIndexPath) {
    const dest = inspection?.status === "draft" ? "setup" : "questions";
    return <Navigate to={dest} replace />;
  }

  return (
    <div className="pt-4 sm:pt-8 px-3 sm:px-6 max-w-5xl mx-auto">
      {sendModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl p-6 max-w-lg w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between mb-4">
              <div className="text-sm font-semibold text-gray-800">
                📧 Send {sendModal.reportType === "final" ? "Final" : sendModal.reportType === "interim" ? "Interim" : "Inspection"} Report
              </div>
              <button
                type="button"
                onClick={() => { setSendModal(null); setSendResult(null); }}
                className="text-gray-400 hover:text-gray-600 text-lg leading-none"
              >
                ✕
              </button>
            </div>

            {/* Attendees list */}
            {attendees.length > 0 && (
              <div className="mb-4">
                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Inspection attendees</div>
                <div className="space-y-1">
                  {attendees.map(a => (
                    <label key={a.email} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedEmails.has(a.email)}
                        onChange={() => toggleAttendee(a.email)}
                        className="rounded"
                      />
                      <span className="font-medium">{a.name}</span>
                      <span className="text-gray-400">{a.email}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}

            {attendees.length === 0 && (
              <div className="text-sm text-gray-400 mb-4">No attendees with email addresses found.</div>
            )}

            {/* Ad-hoc recipients */}
            <div className="mb-4">
              <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Add recipient</div>
              <div className="flex flex-col sm:flex-row gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Name"
                  value={adHocName}
                  onChange={e => setAdHocName(e.target.value)}
                  className="flex-1 border rounded px-2 py-1 text-sm"
                />
                <input
                  type="email"
                  placeholder="Email"
                  value={adHocEmail}
                  onChange={e => setAdHocEmail(e.target.value)}
                  onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); addAdHoc(); } }}
                  className="flex-1 border rounded px-2 py-1 text-sm"
                />
                <button
                  type="button"
                  onClick={addAdHoc}
                  disabled={!adHocName.trim() || !adHocEmail.trim()}
                  className="px-3 py-1 bg-gray-800 text-white rounded text-sm disabled:opacity-40"
                >
                  Add
                </button>
              </div>
              {adHocList.length > 0 && (
                <div className="space-y-1">
                  {adHocList.map(r => (
                    <div key={r.email} className="flex items-center justify-between text-sm bg-gray-50 rounded px-2 py-1">
                      <span><span className="font-medium">{r.name}</span> <span className="text-gray-400">{r.email}</span></span>
                      <button type="button" onClick={() => removeAdHoc(r.email)} className="text-red-400 hover:text-red-600 text-xs ml-2">Remove</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Result feedback */}
            {sendResult && (
              <div className={`rounded-lg px-3 py-2 text-sm mb-4 ${sendResult.failed?.length > 0 ? "bg-amber-50 border border-amber-200 text-amber-800" : "bg-green-50 border border-green-200 text-green-800"}`}>
                {sendResult.message}
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={handleSendReport}
                disabled={sending || (selectedEmails.size === 0 && adHocList.length === 0)}
                className="flex-1 px-4 py-2 rounded-lg bg-blue-700 text-white text-sm font-medium hover:bg-blue-800 disabled:opacity-50 transition"
              >
                {sending ? "Sending…" : "Send report"}
              </button>
              <button
                type="button"
                onClick={() => { setSendModal(null); setSendResult(null); navigate(`/inspections/${inspectionId}/evidence`); }}
                className="px-4 py-2 rounded-lg border text-sm hover:bg-gray-50 transition"
              >
                {sendResult ? "Close" : "Skip"}
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6">
        <div>
          <div className="text-xs text-gray-500 mb-1">Inspection</div>
          <h1 className="text-xl font-semibold">{title}</h1>
          {inspection?.status && (
            <div className="text-sm text-gray-500">Status: {inspection.status}</div>
          )}
        </div>

        <div className="flex flex-wrap gap-2">
        <button
            type="button"
            onClick={() => requestSummary("required", () => openPdf("inspection"))}
            disabled={pdfLoading === "inspection" || !["questions_submitted", "completed"].includes(inspection?.status)}
            title={
              !["questions_submitted", "completed"].includes(inspection?.status)
                ? "Available after questions are submitted"
                : "Download inspection report"
            }
            className="px-3 py-1 border rounded hover:bg-gray-50 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {pdfLoading === "inspection" ? "Generating…" : "⬇ Inspection Report"}
          </button>

          <button
            type="button"
            onClick={() => requestSummary("optional", () => openPdf("final"))}
            disabled={pdfLoading === "final" || inspection?.status !== "completed"}
            title={
              inspection?.status !== "completed"
                ? "Final report available after all findings are closed"
                : "Download final report"
            }
            className="px-3 py-1 border rounded hover:bg-gray-50 text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {pdfLoading === "final" ? "Generating…" : "⬇ Final PDF"}
          </button>

          <button
            type="button"
            onClick={() => navigate("/inspections")}
            className="px-3 py-1 border rounded hover:bg-gray-50 text-sm"
          >
            Back
          </button>
        </div>
      </div>

      {loading && <div className="text-sm text-gray-500">Loading…</div>}

      {!loading && err && (
        <div className="text-sm text-red-600 border border-red-200 rounded p-3 mb-4">
          {err}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6 border-b pb-3">
      {inspection?.questions_locked ? (
          <span
            className="px-3 py-1 rounded text-sm border border-gray-200 text-gray-300 cursor-not-allowed"
            title="Questions locked after Inspection Report is generated"
          >
            1 Questions
          </span>
        ) : (
          <NavLink
            to="questions"
            end
            className={({ isActive }) =>
              `px-3 py-1 rounded text-sm border ${
                isActive ? "bg-gray-900 text-white border-gray-900" : "hover:bg-gray-50"
              }`
            }
          >
            1 Questions
          </NavLink>
        )}

        {(inspection?.questions_locked || inspection?.status === "completed") ? (
          <NavLink
            to="evidence"
            className={({ isActive }) =>
              `px-3 py-1 rounded text-sm border ${
                isActive ? "bg-gray-900 text-white border-gray-900" : "hover:bg-gray-50"
              }`
            }
          >
            2 Evidence
          </NavLink>
        ) : (
          <span
            className="px-3 py-1 rounded text-sm border border-gray-200 text-gray-300 cursor-not-allowed"
            title="Generate inspection report first"
          >
            2 Evidence
          </span>
        )}

        {(inspection?.questions_locked || inspection?.status === "completed") ? (
          <NavLink
            to="closeout"
            className={({ isActive }) =>
              `px-3 py-1 rounded text-sm border ${
                isActive ? "bg-gray-900 text-white border-gray-900" : "hover:bg-gray-50"
              }`
            }
          >
            3 Close Out
          </NavLink>
        ) : (
          <span
            className="px-3 py-1 rounded text-sm border border-gray-200 text-gray-300 cursor-not-allowed"
            title="Generate inspection report first"
          >
            3 Close Out
          </span>
        )}
      </div>

      {/* Summary modal */}
      {summaryModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg mx-4 p-6">
            <div className="text-base font-semibold text-gray-900 mb-1">
              {summaryModal.mode === "required"
                ? "Add an inspection summary"
                : "Update inspection summary"}
            </div>
            <div className="text-sm text-gray-500 mb-3">
              {summaryModal.mode === "required"
                ? "Please write a summary of this inspection. It will appear on page 2 of the report above the open points."
                : "You can update the summary before generating this report. Leave blank to skip."}
            </div>
            <textarea
              autoFocus
              rows={5}
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
              placeholder="Overall inspection summary, general observations, scope notes…"
              value={summaryDraft}
              onChange={(e) => setSummaryDraft(e.target.value)}
            />
            <div className="flex justify-end gap-2 mt-4">
              {summaryModal.mode === "optional" && (
                <button
                  type="button"
                  onClick={() => {
                    const cb = summaryModal.onConfirm;
                    setSummaryModal(null);
                    cb?.();
                  }}
                  className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
                >
                  Skip
                </button>
              )}
              <button
                type="button"
                onClick={() => setSummaryModal(null)}
                className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={saveSummaryAndProceed}
                disabled={false}
                className="px-4 py-2 text-sm bg-blue-700 text-white rounded-lg hover:bg-blue-800 disabled:opacity-40"
              >
                {summaryDraft.trim() ? "Save & generate" : "Generate without summary"}
              </button>
            </div>
          </div>
        </div>
      )}

      <Outlet
        context={{
          inspectionId: Number(inspectionId),
          inspection,
          setInspection,
          token,
          user: ctxUser,
          template,
          answersByQid,
          setAnswersByQid,
          queueSave,
          flushSave,
          setErr,
          setEmailModal: openSendModal,
          requestSummary,
        }}
      />
    </div>
  );
}
